import { useState, useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import locationsService from '../services/locationsService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Initial state for locations data management
 */
const initialState = {
  locations: [],
  loading: false,
  error: null,
  selectedLocationIds: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20,
  },
  filters: {
    search: '',
    state: '',
    city: '',
    location_type: '',
    is_active: '',
  },
  sortConfig: {
    field: 'created_at',
    direction: 'desc',
  },
};

/**
 * Locations reducer for managing complex state updates
 * @param {Object} state - Current state
 * @param {Object} action - Action object
 * @returns {Object} New state
 */
const locationsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_LOCATIONS':
      return {
        ...state,
        locations: action.payload.data || [],
        pagination: {
          ...state.pagination,
          totalCount: action.payload.total || 0,
          totalPages: Math.ceil((action.payload.total || 0) / state.pagination.limit),
        },
        loading: false,
        error: null,
      };

    case 'ADD_LOCATION':
      return {
        ...state,
        locations: [action.payload, ...state.locations],
        pagination: {
          ...state.pagination,
          totalCount: state.pagination.totalCount + 1,
        },
      };

    case 'UPDATE_LOCATION':
      return {
        ...state,
        locations: state.locations.map(location =>
          location.id === action.payload.id ? action.payload : location
        ),
      };

    case 'REMOVE_LOCATION':
      return {
        ...state,
        locations: state.locations.filter(location => location.id !== action.payload),
        selectedLocationIds: state.selectedLocationIds.filter(id => id !== action.payload),
        pagination: {
          ...state.pagination,
          totalCount: Math.max(0, state.pagination.totalCount - 1),
        },
      };

    case 'REMOVE_LOCATIONS':
      const idsToRemove = action.payload;
      return {
        ...state,
        locations: state.locations.filter(location => !idsToRemove.includes(location.id)),
        selectedLocationIds: state.selectedLocationIds.filter(id => !idsToRemove.includes(id)),
        pagination: {
          ...state.pagination,
          totalCount: Math.max(0, state.pagination.totalCount - idsToRemove.length),
        },
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, currentPage: 1 }, // Reset to first page
      };

    case 'SET_SORT':
      return {
        ...state,
        sortConfig: action.payload,
      };

    case 'SET_PAGE':
      return {
        ...state,
        pagination: { ...state.pagination, currentPage: action.payload },
      };

    case 'SELECT_LOCATION':
      const { locationId, selected } = action.payload;
      return {
        ...state,
        selectedLocationIds: selected
          ? [...state.selectedLocationIds, locationId]
          : state.selectedLocationIds.filter(id => id !== locationId),
      };

    case 'SELECT_ALL_LOCATIONS':
      return {
        ...state,
        selectedLocationIds: action.payload
          ? state.locations.map(location => location.id)
          : [],
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedLocationIds: [],
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

/**
 * Hook for managing locations data and operations
 * - Provides comprehensive CRUD operations, pagination, filtering, sorting, and selection
 * - Uses reducer pattern for complex state management
 */
const useLocations = () => {
  const [state, dispatch] = useReducer(locationsReducer, initialState);

  // Message alerts
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  /**
   * Generate a unique signature for current fetch parameters
   * - Used to prevent duplicate API calls with same parameters
   */
  const fetchSignature = useMemo(() => {
    return JSON.stringify({
      filters: state.filters,
      sort: state.sortConfig,
      page: state.pagination.currentPage,
      limit: state.pagination.limit,
    });
  }, [state.filters, state.sortConfig, state.pagination.currentPage, state.pagination.limit]);

  const [lastFetchSignature, setLastFetchSignature] = useState('');
  // Track in-flight fetch signatures to avoid duplicate concurrent requests
  const inFlightRef = useRef(new Set());

  /**
   * Fetch locations from API with current filters, sort, and pagination
   * @param {Object} options - Fetch options
   * @param {boolean} options.force - Force fetch even if signature matches
   */
  const fetchLocations = useCallback(async (options = {}) => {
    const { force = false } = options;
    
    // Prevent duplicate API calls
    if (!force && fetchSignature === lastFetchSignature) {
      return;
    }

    // If an identical request is already in-flight, skip issuing another one
    if (inFlightRef.current.has(fetchSignature)) {
      return;
    }

    // mark this signature as in-flight
    inFlightRef.current.add(fetchSignature);

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const params = {
        ...state.filters,
        page: state.pagination.currentPage,
        limit: state.pagination.limit,
        sort_field: state.sortConfig.field,
        sort_direction: state.sortConfig.direction,
      };

      // Clean up empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const result = await locationsService.getLocations(params);

      if (result.success) {
        dispatch({ type: 'SET_LOCATIONS', payload: result });
        setLastFetchSignature(fetchSignature);
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || i18n.t('hookErrors.locations.fetchList') });
    } finally {
      // clear in-flight marker
      inFlightRef.current.delete(fetchSignature);
    }
  }, [fetchSignature, lastFetchSignature, state.filters, state.pagination.currentPage, state.pagination.limit, state.sortConfig]);

  /**
   * Get location by ID
   * - First checks local state, then fetches from API if not found
   * @param {string|number} locationId - Location ID
   * @param {Object} options - Options
   * @param {boolean} options.withLoading - Whether to show loading state
   */
  const getLocationById = useCallback(async (locationId, options = {}) => {
    const { withLoading = true } = options;

    // First try to find in current state
    const existingLocation = state.locations.find(loc => loc.id === locationId);
    if (existingLocation) {
      return existingLocation;
    }

    // Fetch from API
    if (withLoading) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      const result = await locationsService.getLocationById(locationId);

      if (result.success) {
        return result.data;
      } else {
        if (withLoading) {
          dispatch({ type: 'SET_ERROR', payload: result.error });
        }
        return null;
      }
    } catch (error) {
      const errorMessage = error.message || i18n.t('hookErrors.locations.fetchOne');
      if (withLoading) {
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      }
      return null;
    } finally {
      if (withLoading) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [state.locations]);

  /**
   * Create a new location
   * @param {Object} locationData - Location data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const createLocation = useCallback(async (locationData) => {
    try {
      const result = await locationsService.createLocation(locationData);

      if (result.success) {
        dispatch({ type: 'ADD_LOCATION', payload: result.data });
        // Show success message
  const label = result.data?.delivery_location || result.data?.name || result.data?.id || '';
        try {
          showCreate(
            i18n.t('locations.notifications.created', { name: label })
          );
        } catch (e) {
          // ignore any message errors
        }
        return { success: true, data: result.data };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message || i18n.t('hookErrors.locations.create') };
    }
  }, [showCreate]);

  /**
   * Update an existing location
   * @param {string|number} locationId - Location ID
   * @param {Object} locationData - Updated location data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const updateLocation = useCallback(async (locationId, locationData) => {
    try {
      const result = await locationsService.updateLocation(locationId, locationData);

      if (result.success) {
        dispatch({ type: 'UPDATE_LOCATION', payload: result.data });
        // Show update message
  const label = result.data?.delivery_location || result.data?.name || locationId;
        try {
          showUpdate(
            i18n.t('locations.notifications.updated', { name: label })
          );
        } catch (e) {}
        return { success: true, data: result.data };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message || i18n.t('hookErrors.locations.update') };
    }
  }, [showUpdate]);

  /**
   * Delete a single location
   * @param {string|number} locationId - Location ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const deleteLocation = useCallback(async (locationId) => {
    try {
      const result = await locationsService.deleteLocation(locationId);

      if (result.success) {
        dispatch({ type: 'REMOVE_LOCATION', payload: locationId });
        // Show delete message
        try {
          // Prefer delivery location name from local state if available
          const loc = state.locations.find(l => l.id === locationId) || {};
          const label = loc.delivery_location || loc.name || locationId;
          showDelete(
            i18n.t('locations.notifications.deleted', { name: label })
          );
        } catch (e) {}
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message || i18n.t('hookErrors.locations.deleteOne') };
    }
  }, [showDelete, state.locations]);

  /**
   * Bulk delete multiple locations
   * @param {Array<string|number>} locationIds - Array of location IDs. If not provided, uses selected locations.
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const bulkDeleteLocations = useCallback(async (locationIds = null) => {
    const idsToDelete = locationIds || state.selectedLocationIds;
    
    if (!idsToDelete || idsToDelete.length === 0) {
      return { success: false, error: i18n.t('hookErrors.noSelection.locations') };
    }

    try {
      const result = await locationsService.bulkDeleteLocations(idsToDelete);

      if (result.success) {
        dispatch({ type: 'REMOVE_LOCATIONS', payload: idsToDelete });
        dispatch({ type: 'CLEAR_SELECTION' });
        // Show bulk delete message
        try {
          // Attempt to show delivery location names where possible
          const names = idsToDelete.map(id => {
            const loc = state.locations.find(l => l.id === id);
            return (loc && (loc.delivery_location || loc.name)) || null;
          }).filter(Boolean);

          if (names.length === idsToDelete.length && names.length > 0) {
            const message = i18n.t('locations.notifications.bulkDeletedByName', { names: names.join(', ') });
            showDelete(message);
          } else {
            const message = i18n.t('orders.notifications.bulkDeleted', { count: idsToDelete.length });
            showDelete(message);
          }
        } catch (e) {}
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message || i18n.t('hookErrors.locations.deleteMany') };
    }
  }, [state.selectedLocationIds, showDelete, state.locations]);

  /**
   * Export locations to CSV
   * @param {Object} exportFilters - Export filters (optional)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  const exportLocations = useCallback(async (exportFilters = {}) => {
    try {
      const filters = { ...state.filters, ...exportFilters };
      const result = await locationsService.exportLocations(filters);
      return result;
    } catch (error) {
      return { success: false, error: error.message || i18n.t('hookErrors.locations.export') };
    }
  }, [state.filters]);

  /**
   * Update filters and trigger fetch
   * @param {Object} newFilters - New filter values
   */
  const updateFilters = useCallback((newFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: newFilters });
  }, []);

  /**
   * Update sort configuration and trigger fetch
   * @param {Object} sortConfig - Sort configuration
   */
  const updateSort = useCallback((sortConfig) => {
    dispatch({ type: 'SET_SORT', payload: sortConfig });
  }, []);

  /**
   * Go to specific page
   * @param {number} page - Page number
   */
  const goToPage = useCallback((page) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  /**
   * Go to next page
   */
  const nextPage = useCallback(() => {
    if (state.pagination.currentPage < state.pagination.totalPages) {
      dispatch({ type: 'SET_PAGE', payload: state.pagination.currentPage + 1 });
    }
  }, [state.pagination.currentPage, state.pagination.totalPages]);

  /**
   * Go to previous page
   */
  const prevPage = useCallback(() => {
    if (state.pagination.currentPage > 1) {
      dispatch({ type: 'SET_PAGE', payload: state.pagination.currentPage - 1 });
    }
  }, [state.pagination.currentPage]);

  /**
   * Select or deselect a location
   * @param {string|number} locationId - Location ID
   * @param {boolean} selected - Whether to select or deselect
   */
  const selectLocation = useCallback((locationId, selected) => {
    dispatch({ type: 'SELECT_LOCATION', payload: { locationId, selected } });
  }, []);

  /**
   * Select or deselect all locations
   * @param {boolean} selected - Whether to select or deselect all
   */
  const selectAllLocations = useCallback((selected) => {
    dispatch({ type: 'SELECT_ALL_LOCATIONS', payload: selected });
  }, []);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  /**
   * Check if a location is selected
   * @param {string|number} locationId - Location ID
   * @returns {boolean} Whether location is selected
   */
  const isLocationSelected = useCallback((locationId) => {
    return state.selectedLocationIds.includes(locationId);
  }, [state.selectedLocationIds]);

  /**
   * Check if all locations are selected
   * @returns {boolean} Whether all locations are selected
   */
  const isAllSelected = useMemo(() => {
    return state.locations.length > 0 && state.selectedLocationIds.length === state.locations.length;
  }, [state.locations.length, state.selectedLocationIds.length]);

  /**
   * Check if there are any locations
   * @returns {boolean} Whether there are locations
   */
  const hasLocations = useMemo(() => {
    return state.locations.length > 0;
  }, [state.locations.length]);

  /**
   * Check if there are any selected locations
   * @returns {boolean} Whether there are selected locations
   */
  const hasSelection = useMemo(() => {
    return state.selectedLocationIds.length > 0;
  }, [state.selectedLocationIds.length]);

  /**
   * Get count of selected locations
   * @returns {number} Number of selected locations
   */
  const selectionCount = useMemo(() => {
    return state.selectedLocationIds.length;
  }, [state.selectedLocationIds.length]);

  // Fetch locations when dependencies change
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  return {
    // Data
    locations: state.locations,
    loading: state.loading,
    error: state.error,
    pagination: state.pagination,
    filters: state.filters,
    sortConfig: state.sortConfig,
    selectedLocationIds: state.selectedLocationIds,

    // Actions
    fetchLocations,
    getLocationById,
    createLocation,
    updateLocation,
    deleteLocation,
    bulkDeleteLocations,
    exportLocations,

    // Filters and sorting
    updateFilters,
    updateSort,

    // Pagination
    goToPage,
    nextPage,
    prevPage,
    currentPage: state.pagination.currentPage,
    totalPages: state.pagination.totalPages,
    totalCount: state.pagination.totalCount,

    // Selection
    selectLocation,
    selectAllLocations,
    clearSelection,
    isLocationSelected,
    isAllSelected,
    hasSelection,
    selectionCount,

    // Utilities
    hasLocations,
    clearError,
  };
};

export default useLocations;